import { HTTP_INTERCEPTORS, provideHttpClient, withFetch, withInterceptorsFromDi } from '@angular/common/http';
import { APP_ID, APP_INITIALIZER, ErrorHandler, InjectionToken } from '@angular/core';
import { provideClientHydration } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import {
  Router,
  provideRouter,
  withEnabledBlockingInitialNavigation,
  withInMemoryScrolling,
  withRouterConfig,
} from '@angular/router';
import { TraceService } from '@sentry/angular-ivy';
import { CookieService } from 'ngx-cookie-service';
import { environment } from '../environments/environment';
import { AuthInterceptor } from './auth/interceptors/auth-interceptor';
import { ErrorInterceptor } from './auth/interceptors/error-interceptor';
import { ErrorRetryInterceptor } from './auth/interceptors/error-retry-interceptor';
import { ChromeExtensionRoutes } from './chrome-extension-routes';
import { CustomErrorHandlerService } from './error-handler';
import { SentryTransactionInterceptor } from './interceptors/sentry-transaction-interceptor';
import { AppConfigService } from './providers/config.service';
import { routes } from './routes';
import { AnalyticsService } from './services/analytics.service';
import { BrowserService } from './services/browser.service';
import { MetaService } from './services/meta.service';
import { ScrollService } from './services/scroll.service';
import { tenantRoutes } from './tenant-routes';

function initializeApp(
  scroll: ScrollService,
  an: AnalyticsService,
  meta: MetaService,
  config: AppConfigService,
  browserService: BrowserService,
  router: Router
) {
  return () => {
    const routes = getRoutes(browserService);
    if (routes) router.resetConfig(routes);
    console.debug('ANGULAR_INIT_APP');
  };
}

function getRoutes(browserService: BrowserService) {
  const isTenantDomain = browserService.isTenantDomain();
  if (browserService.isExtension()) {
    return ChromeExtensionRoutes;
  } else {
    if (isTenantDomain) return tenantRoutes;
  }
}

import { GrowthBook } from '@growthbook/growthbook';
import { GROWTHBOOK } from './growthbook';
export const HAPPY_DOM = new InjectionToken<Window>('happyDom');

// Create a GrowthBook instance
const gb = (config: AppConfigService, analytics: AnalyticsService) => {
  return new GrowthBook({
    apiHost: 'https://cdn.growthbook.io',
    clientKey: config.growthBookKey,
    // Enable easier debugging during development
    enableDevMode: false,
    // Update the instance in realtime as features change in GrowthBook
    subscribeToChanges: false,
    // Targeting attributes
    realtimeInterval: 1,
    attributes: {},
    backgroundSync: false,
    // Only required for A/B testing
    // Called every time a user is put into an experiment
    trackingCallback: (experiment, result) => {
      analytics.expertimentViewed(experiment.key, result.key);
    },
  });
};

export const appConfig = {
  providers: [
    provideHttpClient(withFetch(), withInterceptorsFromDi()),
    ...(environment.extension ? [] : [provideClientHydration()]),
    provideAnimations(),
    CookieService,
    { provide: APP_ID, useValue: 'picflow' },
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: SentryTransactionInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ErrorRetryInterceptor, multi: true },
    { provide: GROWTHBOOK, useFactory: gb, deps: [AppConfigService, AnalyticsService] },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeApp,
      deps: [ScrollService, AnalyticsService, MetaService, AppConfigService, BrowserService, Router],
      multi: true,
    },
    provideRouter(
      routes,
      withEnabledBlockingInitialNavigation(),
      withRouterConfig({
        onSameUrlNavigation: 'reload',
      }),
      withInMemoryScrolling({ scrollPositionRestoration: 'enabled', anchorScrolling: 'enabled' })
    ),
    { provide: ErrorHandler, useClass: CustomErrorHandlerService },
    {
      provide: TraceService,
      deps: [Router],
    },
  ],
};
